import { DoorSlat } from './DoorSlat';
import {
  THICKNESS_PANE_DEFAULT,
  PlateType,
  CornerType,
  DoorHandleType,
  DrawerHandleType,
  ECO_COLOR,
  ECO_PLUS_COLOR,
  MINIMUM_WIDTH_FOR_TWO_DRAWER_HANDLES,
  PaneMaterial,
  Position,
  PositionAngle,
  PositionLargeDoor,
  PositionLateral,
  BATTEN_WIDTH,
  BATTEN_DEPTH,
  NATURAL_COLOR,
} from '../constants';

import { Batten } from './Batten';
import { Corner, getOldReferenceColor } from './Corner';
import { Pane } from './Pane';
import { Plate } from './Plate';
import { Door } from './Door';
import { CrossPiece } from './CrossPiece';
import { CustomPieceJson } from '../Data';
import { PiecesComponentsList } from '../entities/PiecesComponentsList';
import { Drawer } from './Drawer';
import { PositionPiece } from '../type';
import { IntermediateShelf } from './IntermediateShelf';
import { Rod } from './Rod';
import { DoorHandle } from './DoorHandle';
import { DrawerHandle } from './DrawerHandle';
import { DrawerSlat } from './DrawerSlat';
import { Color } from '../entities/Color';
import { SupportIntermediateShelf } from './SupportIntermediateShelf';
import { getColors, getPiecesData } from '../utils/dataStorage';
import { getMaterialColor, hasMaterialColor } from '../utils/colorHelper';
import { SimpleComponent } from './SimpleComponent';
import { ComponentKewlox } from './ComponentKewlox';
import { CrossPieceReinforcement } from './CrossPieceReinforcement';
import { WheelsBase } from './WheelsBase';

export type KewloxPieceType =
  | Batten
  | Corner
  | Pane
  | Plate
  | Door
  | DoorHandle
  | Drawer
  | CrossPiece
  | IntermediateShelf
  | SupportIntermediateShelf
  | Rod
  | DrawerHandle
  | DrawerSlat
  | SimpleComponent
  | WheelsBase;

export type Handle =
  | DoorHandle
  | CustomPiece<DrawerHandle>[]
  | CustomPiece<DrawerSlat>;

export class CustomPiece<T extends KewloxPieceType> {
  base: T;
  custom: boolean;
  position: PositionPiece;
  plateType?: PlateType;
  private _height?: number;
  private _width?: number;
  private _depth?: number;
  private _handle?: Handle;
  private _doorSlats?: DoorSlat[];
  private _color?: Color;
  private _openDoor?: number;
  private _quantity?: number;

  constructor(
    base: T,
    custom: boolean,
    position: PositionPiece,
    height?: number,
    width?: number,
    depth?: number,
    handle?: Handle,
    color?: Color,
    quantity = 1,
    plateType?: PlateType,
  ) {
    this.base = base;
    this.custom = custom;
    this.position = position;
    if (color) this._color = color;
    else {
      if (this.base.hasOwnProperty('color')) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        this._color = this.base.color;
      }
    }

    this.plateType = plateType;
    if (base instanceof Plate && !custom) this.plateType = base.type;

    if (height) this._height = height;
    if (width) this._width = width;
    if (depth) this._depth = depth;
    if (handle) this._handle = handle;
    if (quantity) this._quantity = quantity;

    this.adaptHandleForDoorSlat();
  }

  get doorIsInside() {
    if (!(this.base instanceof Door))
      throw new Error('Not door for doorIsInside');

    if ((this.base as Door).largeDoorPosition)
      return (this.base as Door).largeDoorPosition === PositionLargeDoor.Inside;

    return !(
      this.position === PositionAngle.BackRight ||
      this.position === PositionAngle.FrontRight
    );
  }

  toJSON() {
    const obj = Object.assign({}, this, {
      base: this.base.id,
      height: this._height,
    });
    if (this.custom && this.realHeight) {
      Object.assign(obj, { realHeight: this.realHeight });
    }

    if (this._width) {
      Object.assign(obj, { width: this._width });
    }
    if (this.custom && this.realWidth) {
      Object.assign(obj, { realWidth: this.realWidth });
    }

    if (this.calibrated) {
      Object.assign(obj, { calibrated: this.calibrated });
    }

    if (this._depth) {
      Object.assign(obj, { depth: this._depth });
    }
    if (this.custom && this.realDepth) {
      Object.assign(obj, { realDepth: this.realDepth });
    }

    if (this._color) {
      Object.assign(obj, { color: this._color.id });
    }
    if (this._handle) {
      if (this._handle instanceof DoorHandle) {
        Object.assign(obj, { handle: this._handle.id });
      } else if (!Array.isArray(this._handle)) {
        Object.assign(obj, { slat: this._handle });
      } else {
        Object.assign(obj, { handle: this._handle });
      }
    }
    if (this._quantity !== undefined && this._quantity > 1) {
      Object.assign(obj, { quantity: this._quantity });
    }
    return obj;
  }

  get height(): number {
    if (this.custom && this._height !== undefined) return this._height || 0;
    return this.base.height;
  }

  set height(value: number) {
    this._height = value;
  }

  get width(): number {
    if (this.custom && this._width !== undefined) return this._width || 0;
    if (this.base.hasOwnProperty('width')) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      return this.base.width;
    }
    return 0;
  }

  set width(value: number) {
    this._width = value;
  }

  get depth(): number {
    if (this.custom && this._depth !== undefined) return this._depth || 0;
    if (this.base.hasOwnProperty('depth')) {
      // @ts-ignore
      return this.base.depth;
    }
    return 0;
  }

  set depth(value: number) {
    this._depth = value;
  }

  get quantity(): number {
    return this._quantity ?? 1;
  }

  set quantity(value: number) {
    this._quantity = value;
  }

  addQuantity() {
    if (this._quantity === undefined) this._quantity = 1;
    this._quantity += 1;
  }

  get handle(): Handle | undefined {
    return this._handle;
  }

  set handle(value: Handle | undefined) {
    this._handle = value;
  }

  get handles(): CustomPiece<DrawerHandle>[] | null {
    if (Array.isArray(this._handle)) {
      return this._handle as CustomPiece<DrawerHandle>[];
    }
    return null;
  }

  get numberOfHandles(): number {
    if (
      this.color.reference === ECO_COLOR ||
      this.color.reference === ECO_PLUS_COLOR
    )
      return this.width >= MINIMUM_WIDTH_FOR_TWO_DRAWER_HANDLES ? 2 : 1;

    if (Array.isArray(this.handle)) return this.handle.length;

    return this.handle ? 1 : 0;
  }

  getDrawerHandle(
    position: PositionAngle | Position,
  ): CustomPiece<DrawerHandle> | CustomPiece<DrawerSlat> | undefined {
    if (
      this._handle &&
      Array.isArray(this._handle) &&
      this._handle.length > 0 &&
      this._handle[0] instanceof CustomPiece
    ) {
      const handle = (this._handle as Array<CustomPiece<DrawerHandle>>).find(
        e => e.position === position,
      );
      if (!handle) throw new Error('Position not found in the drawer');
      return handle;
    }

    if (
      this._handle &&
      (this._handle as CustomPiece<DrawerSlat>).position !== position
    )
      throw new Error('Position not found in the drawer');

    if (this._handle instanceof DoorHandle) throw new Error('Door handle');

    return this._handle as undefined | CustomPiece<DrawerSlat>;
  }

  get description(): string {
    if (!this.custom) {
      if (!this._color) {
        return this.base.getKey();
      } else {
        const data = JSON.parse(this.base.getKey());
        data.color = this._color.id;
        return JSON.stringify(data);
      }
    } else {
      return `${this.base.getKey()} ${this.height}x${this._width}`;
    }
  }

  get weight(): number {
    let weight: number | undefined = 0;

    weight = this.base.computeWeight(this);
    if (weight) {
      if (this._doorSlats && this._doorSlats.length > 0) {
        weight += (this._doorSlats as DoorSlat[]).reduce(
          (accumulator: number, currentValue: DoorSlat) =>
            accumulator + currentValue.computeWeight(),
          0,
        );
      }

      if (this.base instanceof CrossPiece) {
        //@ts-ignore
        const reinforce = this.base.reinforce;
        if (reinforce) {
          const cpr = new CrossPieceReinforcement(this.width, this.custom);
          weight += cpr.computeWeight();
        }
      }

      return weight;
    }

    return this.base.weight;
  }

  hasPaneMaterial(): boolean {
    return (
      this.base instanceof Plate ||
      this.base instanceof Pane ||
      this.base instanceof Door ||
      this.base instanceof IntermediateShelf ||
      this.base instanceof SupportIntermediateShelf
    );
  }

  get realWidth(): number {
    if (this.base instanceof Batten) {
      //@ts-ignore
      return this.base.constructor.computeRealDepth(BATTEN_WIDTH);
    }
    if (this.base instanceof SupportIntermediateShelf) {
      //@ts-ignore
      return this.base.realWidth;
    }
    const hasLargeDoor = this.base.hasOwnProperty('largeDoorPosition');

    //@ts-ignore
    const isLargeDoor = hasLargeDoor && this.base.largeDoorPosition;

    //@ts-ignore
    return this.base.constructor.computeRealWidth(
      this.width,
      this.hasPaneMaterial() &&
        (this.material === PaneMaterial.Glass ||
          this.material === PaneMaterial.Mirror),
      isLargeDoor,
    );
  }

  get realHeight(): number {
    if (this.base instanceof CrossPiece) {
      //@ts-ignore
      return this.base.realHeight;
    }

    //@ts-ignore
    return this.base.constructor.computeRealHeight(
      this.height,
      this.plateType,
      this.hasPaneMaterial() &&
        (this.material === PaneMaterial.Glass ||
          this.material === PaneMaterial.Mirror),
    );
  }

  get realDepth(): number {
    if (this.base instanceof Batten) {
      //@ts-ignore
      return this.base.constructor.computeRealDepth(BATTEN_DEPTH);
    }

    if (this.base instanceof IntermediateShelf) {
      //@ts-ignore
      return this.base.constructor.computeRealDepth(
        this.base.depth,
        undefined,
        this.width < 620 ? PlateType.PanePane : PlateType.DoorsPane,
        this.hasPaneMaterial() &&
          (this.material === PaneMaterial.Glass ||
            this.material === PaneMaterial.Mirror),
      );
    }

    if (this.base instanceof Plate) {
      //@ts-ignore
      return this.base.constructor.computeRealDepth(
        this.base.thickness,
        this.color.reference === NATURAL_COLOR,
      );
    }

    if (this.base instanceof Pane) {
      //@ts-ignore
      return this.base.constructor.computeRealDepth(
        THICKNESS_PANE_DEFAULT,
        this.color.reference === NATURAL_COLOR,
      );
    }

    if (this.base instanceof Door) {
      //@ts-ignore
      return this.base.constructor.computeRealDepth(
        THICKNESS_PANE_DEFAULT,
        this.color.reference === NATURAL_COLOR,
      );
    }

    if (this.base instanceof CrossPiece) {
      //@ts-ignore
      return this.base.realDepth;
    }

    //@ts-ignore
    return this.base.constructor.computeRealDepth(
      this.depth,
      this.color.reference === NATURAL_COLOR,
    );
  }

  get price(): number {
    /*if (!this.custom && process.env.NODE_ENV !== 'development') {
      return this.base.price;
    }*/

    return this.computedPrice;
  }

  get computedPrice(): number {
    let price: number | undefined = 0;
    // TODO: Move above to not return base price
    price = this.base.computePrice(this);
    if (price) {
      if (this._doorSlats && this._doorSlats.length > 0)
        price += (this._doorSlats as DoorSlat[]).reduce(
          (accumulator: number, currentValue: DoorSlat) =>
            accumulator + currentValue.computePrice(),
          0,
        );

      if (this.base instanceof CrossPiece) {
        //@ts-ignore
        const reinforce = this.base.reinforce;
        if (reinforce) {
          const cpr = new CrossPieceReinforcement(this.width, this.custom);
          price += cpr.computePrice();
        }
      }

      return price;
    }

    if (!this.custom) {
      return this.base.price;
    }

    return this.base.price * 1.4;
  }

  get priceHandles(): number {
    let price = 0;
    if (typeof this.handle !== 'undefined') {
      if (Array.isArray(this.handle)) {
        for (const handle of this.handle) {
          price += handle.price;
        }
      } else {
        price += this.handle.price;
      }
    }
    return price;
  }

  get weightHandles(): number {
    let weight = 0;
    if (typeof this.handle !== 'undefined') {
      if (Array.isArray(this.handle)) {
        for (const handle of this.handle) {
          weight += handle.weight;
        }
      } else {
        weight += this.handle.weight;
      }
    }
    return weight;
  }

  get priceTaxIncl(): number {
    //TODO - Get the exact VAT number from Redux
    return this.price * 1.21;
  }

  get referenceMaterial(): string {
    if (this.base.hasOwnProperty('color')) {
      if (this.hasOwnProperty('_color')) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        return this._color.reference;
      }
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      return this.base.color.reference;
    }
    return '';
  }

  replaceColor(baseRef: string): string {
    if (this._color) {
      const regex = /\[(.)+\]/;
      return baseRef.replace(regex, `[${this._color.reference}]`);
    } else return baseRef;
  }

  get reference(): string {
    /**
     * Get the reference of the Piece, if this piece is custom, generate one
     */

    if (!this.custom) {
      if (this._color) {
        return this.replaceColor(this.base.reference);
      } else return this.base.reference;
    }

    const baseType = this.base.typeComponent;
    const key = baseType.substr(0, 4);

    let keyName = key.toUpperCase();
    if (
      this.base instanceof Plate &&
      this.base.thickness === THICKNESS_PANE_DEFAULT
    )
      keyName = 'PANE';

    let dimension = '';
    if (this.realHeight && this.realWidth)
      dimension = [this.realWidth, this.realHeight].join('x');
    if (this.realHeight && this.realDepth) {
      dimension = [this.realHeight, this.realDepth].join('x');
    }
    if (this.realHeight && this.realWidth && this.realDepth) {
      dimension = [this.realWidth, this.realHeight, this.realDepth].join('x');
    }
    if (dimension === '') {
      dimension = this.realHeight ? `${this.realHeight}` : `${this.realWidth}`;
    }
    if (this.base instanceof Batten) dimension = `${this.realHeight}`;
    let mat = '';
    if (this.referenceMaterial !== '') mat = `[${this.referenceMaterial}]`;

    let type = '';
    if (this.base.hasOwnProperty('type')) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      type = `-${this.base.type}`;
    }

    let positionHole = '';
    if (this.base.hasOwnProperty('positionHole')) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      if (this.base.positionHole) positionHole = `-${this.base.positionHole}`;
    }

    let reinforce = '';
    if (this.base.hasOwnProperty('reinforce')) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      if (this.base.reinforce) reinforce = 'R';
    }

    // TODO : When adding Custom piece mode, we must handle large door position and Slat material for large door
    // let positionLargeDoor = '';
    // if (this.base.hasOwnProperty('largeDoorPosition')) {
    //   // @ts-ignore
    //   positionLargeDoor = `-${this.base.positionLargeDoor}`;
    // }

    if (this.base instanceof Corner) {
      if (this.base.type === CornerType.Normal) {
        return `U${dimension}-${getOldReferenceColor(this.base.color)}`;
      } else {
        let ref = 'U';
        if (this.base.type === CornerType.Hook) {
          if (this.base.positionHole === Position.Left) {
            ref += `${dimension}-${getOldReferenceColor(this.base.color)} DG`;
          } else
            ref += `${dimension}-${getOldReferenceColor(this.base.color)} DD`;
        } else if (this.base.type === CornerType.Front) {
          ref += `${dimension}-${getOldReferenceColor(this.base.color)} F`;
        } else {
          throw new Error('Drilled not supported');
        }
        return ref;
      }
    }

    if (this.calibrated && !(this.base instanceof Batten)) {
      let baseRef = this.base.reference;
      if (this._color) baseRef = this.replaceColor(this.base.reference);
      const regex = /\[(.)+\]/;
      const refColor = baseRef.match(regex);
      let colorInfo = '';
      if (refColor) {
        baseRef = baseRef.replace(regex, '');
        colorInfo = refColor[0];
      }

      return `${baseRef}-CAL(${dimension})${colorInfo}`;
    }

    // Feet
    if (this.base instanceof Batten && this.base.feet) {
      keyName = 'TASP';
      mat = `-${this.base.material}`;
    }

    return `${keyName}-${dimension}${type}${positionHole}${mat}${reinforce}`;
  }

  get lateralPosition(): PositionLateral | undefined {
    if (
      this.position === PositionAngle.FrontLeft ||
      this.position === PositionAngle.BackLeft
    )
      return PositionLateral.Left;

    if (
      this.position === PositionAngle.FrontRight ||
      this.position === PositionAngle.BackRight
    )
      return PositionLateral.Right;

    return undefined;
  }

  get color(): Color {
    if (this.base.hasOwnProperty('color') && this._color) {
      // @ts-ignore
      return this._color;
    } else {
      throw new Error(`The CustomPiece don't have a color`);
    }
  }

  hasMaterial() {
    if (this.base.hasOwnProperty('color') && this._color) {
      // @ts-ignore
      return hasMaterialColor(this._color);
    }

    return false;
  }

  get material(): PaneMaterial {
    if (this.base.hasOwnProperty('color') && this._color) {
      // @ts-ignore
      return getMaterialColor(this._color);
    } else {
      throw new Error(
        `The CustomPiece don't have a color for material request`,
      );
    }
  }

  set open(value: number) {
    this._openDoor = value;
  }

  get open(): number {
    if (this._openDoor) return this._openDoor;
    return 0;
  }

  get doorHandleType(): DoorHandleType | null {
    /**
     * Return the handle type of the custom piece if this piece have a handle
     */
    if (!(this.base instanceof Door))
      throw new Error('Not door for doorHandleType');

    if (
      this.material === PaneMaterial.Glass ||
      this.material === PaneMaterial.Mirror
    ) {
      // MOVED in rules
      /*
      if (
        this.material === PaneMaterial.Glass &&
        !(this.base as Door).largeDoorPosition &&
        (this.base as Door).slatMaterial
      )
        throw new Error('No slat for glass');

      if (this.material === PaneMaterial.Glass && this.handle)
        throw new Error('No handle for mirror');
        */

      if (
        (this.material === PaneMaterial.Glass ||
          this.material === PaneMaterial.Mirror) &&
        !(this.base as Door).largeDoorPosition
      )
        return DoorHandleType.GlassHole;
    }

    if ((this.base as Door).slatMaterial) {
      // MOVED in rules
      // if (this.handle) throw new Error('No handle for slat door');

      return DoorHandleType.Slat;
    }

    if (this.handle) return (this.handle as DoorHandle).type;

    return null;
  }

  isDrawer(): boolean {
    return this.base instanceof Drawer;
  }

  get drawerHandlesType(): DrawerHandleType {
    if (
      (this.color.reference === ECO_COLOR ||
        this.color.reference === ECO_PLUS_COLOR) &&
      this.handle
    )
      throw new Error('No handle for ECO drawer');

    if (this.color.reference === ECO_COLOR) return DrawerHandleType.Eco;
    if (this.color.reference === ECO_PLUS_COLOR)
      return DrawerHandleType.EcoPlus;

    let drawerHandle = null;
    if (Array.isArray(this.handle) && this.handle.length === 2)
      drawerHandle = this.getDrawerHandle(PositionAngle.FrontLeft);
    else drawerHandle = this.getDrawerHandle(Position.Front);

    if (
      drawerHandle instanceof CustomPiece &&
      drawerHandle.base instanceof DrawerSlat
    )
      return DrawerHandleType.Slat;
    if (
      drawerHandle instanceof CustomPiece &&
      drawerHandle.base instanceof DrawerHandle
    )
      return drawerHandle.base.type;

    throw new Error('Type not found for drawer');
  }

  static fromModel<T extends KewloxPieceType>(
    model: CustomPieceJson,
    piecesComponentsList: PiecesComponentsList<T>,
    plateType?: PlateType,
  ): CustomPiece<T> {
    const { base: baseId, custom, position } = model;
    const height = model.height || undefined;
    const width = model.width || undefined;
    const depth = model.depth || undefined;
    const handleJson = model.handle || undefined;
    const quantity = model.quantity || 1;
    let color = undefined;
    if (model.color) {
      if (typeof model.color === 'number') {
        const colors = getColors();
        color = colors[0];
        for (const colorItem of colors) {
          if (colorItem.id === model.color) {
            color = colorItem;
            break;
          }
        }
      } else {
        const c = Object.create(Color.prototype);
        color = Object.assign(c, this, model.color);
      }
    }

    const baseObj = piecesComponentsList.get(baseId);
    if (!baseObj) {
      throw new Error(
        'Base pieces for this customPiece should exist in the local database',
      );
    }

    let handle = undefined;
    const pieces = getPiecesData();
    if (baseObj instanceof Door && handleJson) {
      const pieces = getPiecesData();
      handle = pieces.doorsHandles.get(handleJson as number);
    } else if (
      baseObj instanceof Drawer &&
      handleJson &&
      Object.keys(handleJson).length > 0
    ) {
      // Can be Handle[] or Slat
      if (Array.isArray(handleJson)) {
        handle = [];
        for (const dHandle of handleJson) {
          handle.push(
            CustomPiece.fromModel<DrawerHandle>(
              dHandle as CustomPieceJson,
              pieces.drawersHandles,
            ),
          );
        }
        handle.sort(CustomPiece.compareTo);
      }
    } else if (baseObj instanceof Drawer && model.slat) {
      handle = CustomPiece.fromModel<DrawerSlat>(
        model.slat as CustomPieceJson,
        pieces.drawersSlat,
      );
    }

    return new CustomPiece<T>(
      baseObj,
      custom,
      position as Position,
      height,
      width,
      depth,
      handle,
      color,
      quantity,
      plateType,
    );
  }

  get calibrated(): boolean {
    // @ts-ignore-start
    if (this.height && this.base) {
      // @ts-ignore
      const realHeight = this.base.constructor.computeRealHeight(
        this.height,
        this.plateType,
      );
      // @ts-ignore
      const baseRealHeight = this.base.constructor.computeRealHeight(
        this.base.height,
        this.base instanceof Plate ? (this.base as Plate).type : undefined,
      );
      if (realHeight > baseRealHeight) return false;
    }
    if (this.width && this.base.hasOwnProperty('width')) {
      // @ts-ignore
      const realWeidth = this.base.constructor.computeRealWidth(
        this.width,
        this.hasPaneMaterial() &&
          (this.material === PaneMaterial.Glass ||
            this.material === PaneMaterial.Mirror),
        this.base.hasOwnProperty('largeDoorPosition') ? true : false,
      );
      const bc = this.base.constructor;
      // @ts-ignore
      const baseWidth = this.base.width;
      // @ts-ignore
      const baseRealWidth = bc.computeRealWidth(
        baseWidth,
        this.hasPaneMaterial() &&
          (this.material === PaneMaterial.Glass ||
            this.material === PaneMaterial.Mirror),
        this.base.hasOwnProperty('largeDoorPosition') ? true : false,
      );
      if (realWeidth > baseRealWidth) return false;
    }
    if (this.depth && this.base.hasOwnProperty('depth')) {
      // @ts-ignore
      const realDepth = this.base.constructor.computeRealDepth(this.depth);
      const bc = this.base.constructor;
      // @ts-ignore
      const baseRealDepth = bc.computeRealDepth(this.base.depth);
      if (realDepth > baseRealDepth) return false;
    }
    // @ts-ignore-end

    return this.custom;
  }

  static compareTo(
    a: CustomPiece<ComponentKewlox>,
    b: CustomPiece<ComponentKewlox>,
  ) {
    if (a.position < b.position) return 1;
    else if (a.position > b.position) return -1;
    return 0;
  }

  adaptHandleForDoorSlat() {
    if (this.base instanceof Door) {
      if (this.base.slatMaterial) {
        const ds = new DoorSlat(
          this.height,
          this.base.slatMaterial,
          this.material,
          this.custom,
        );
        this._doorSlats = [ds, ds];
      } else this._doorSlats = undefined;
    }
  }
}
